import React, { useState } from "react";
import { Platform, Text, TouchableOpacity, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import {
  formatMetric,
  metricValue,
  MockGrain,
  MockLocationRow,
  MockMetric,
} from "./LocationSummaryV2MockData";
import { v2Styles as styles } from "./LocationSummaryV2.styles";
import { useTheme } from "../../../../../theme/useTheme";
import { ColumnWidths } from "./LocationSummaryV2Table.shared";
import { BORDERED_GRAINS } from "./LocationSummaryTableHeader";

interface MetricColumn {
  key: MockMetric;
  label: string;
}

interface Props {
  row: MockLocationRow;
  grains: MockGrain[];
  metrics: readonly MetricColumn[];
  hierarchyMode: "flat" | "region" | "market";
  effectiveExpandedGroups: Set<string>;
  selectedId?: string;
  columnWidths: ColumnWidths;
  onToggleGroup: (row: MockLocationRow) => void;
  onSelect: (row: MockLocationRow) => void;
}

const GRAIN_FIELD_MAP: Record<MockGrain, (row: MockLocationRow) => string> = {
  region:       (row) => row.region,
  market:       (row) => row.market,
  location:     (row) => row.location,
  locationType: (row) => row.locationType,
  year:         (row) => row.year,
  period:       (row) => row.period,
  week:         (row) => row.week,
};

type HierarchyGroupType = NonNullable<MockLocationRow["groupType"]>;

// Which row field a grain column reads for each hierarchy level, per hierarchy
// mode. "region" mode has one visible column, so every level renders its own
// field; "market" mode has two, so the region column always shows the row's
// own region (parent context) and the market column nests the location value
// for location-level rows (there is no dedicated location column in that mode).
const HIERARCHY_VALUE_SOURCE: Partial<
  Record<
    "region" | "market",
    Partial<
      Record<MockGrain, Record<HierarchyGroupType, keyof MockLocationRow>>
    >
  >
> = {
  region: {
    region: { region: "region", market: "market", location: "location" },
  },
  market: {
    region: { region: "region", market: "region", location: "region" },
    market: { region: "market", market: "market", location: "location" },
  },
};

export const hierarchyCellLabel = (
  row: MockLocationRow,
  grain: MockGrain,
  hierarchyMode: "flat" | "region" | "market"
): string => {
  if (hierarchyMode === "flat" || row.groupType == null) {
    return GRAIN_FIELD_MAP[grain](row) || "";
  }
  const fieldKey =
    HIERARCHY_VALUE_SOURCE[hierarchyMode]?.[grain]?.[row.groupType];
  return (
    (fieldKey ? (row[fieldKey] as string) : GRAIN_FIELD_MAP[grain](row)) || ""
  );
};

const isWeb = Platform.OS === "web";

const LocationSummaryTableRow: React.FC<Props> = ({
  row,
  grains,
  metrics,
  hierarchyMode,
  effectiveExpandedGroups,
  selectedId,
  columnWidths,
  onToggleGroup,
  onSelect,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [rowHovered, setRowHovered] = useState(false);
  // Track which grain cell is hovered for the underline effect.
  const [hoveredGrain, setHoveredGrain] = useState<MockGrain | null>(null);

  const rowWebProps = isWeb
    ? ({
        onMouseEnter: () => setRowHovered(true),
        onMouseLeave: () => { setRowHovered(false); setHoveredGrain(null); },
      } as any)
    : {};

  return (
    <TouchableOpacity
      key={row.id}
      style={[
        styles.tableRow,
        { width: "100%", backgroundColor: "#FFFFFF" },
        row.groupType === "region" && styles.hierarchyRegion,
        row.groupType === "market" && styles.hierarchyMarket,
        row.groupType === "location" && styles.hierarchyLocation,
        selectedId === row.id && styles.selectedRow,
        // Web row-hover background
        isWeb && rowHovered && !selectedId && {
          backgroundColor: isDark ? colors.background.elevated : "#e7effb",
        },
      ]}
      onPress={() => {
        // Region/Market rows both expand their hierarchy AND anchor the
        // companion table to that region/market's locations; location leaves
        // only ever select (nothing left to expand).
        if (hierarchyMode !== "flat" && row.groupType !== "location") {
          onToggleGroup(row);
        }
        onSelect(row);
      }}
      testID={`asn-v2-row-${row.id}`}
      {...rowWebProps}
    >
      {grains.map((grain, index) => {
        const isCellHovered = isWeb && hoveredGrain === grain;
        const cellWebProps = isWeb
          ? ({
              onMouseEnter: () => setHoveredGrain(grain),
              onMouseLeave: () => setHoveredGrain(null),
            } as any)
          : {};
        return (
          <View
            key={grain}
            style={[
              styles.cell,
              { width: columnWidths.grain },
              BORDERED_GRAINS.has(grain) && styles.columnDivider,
              row.groupType === "market" &&
                hierarchyMode !== "flat" &&
                styles.hierarchyIndent,
              row.groupType === "location" &&
                hierarchyMode !== "flat" &&
                styles.hierarchyMarketIndent,
              // Pointer cursor on web
              isWeb && ({ cursor: "pointer" } as any),
            ]}
            {...cellWebProps}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              {hierarchyMode !== "flat" &&
              row.groupType !== "location" &&
              ((hierarchyMode === "region" &&
                grains.length === 1 &&
                index === 0) ||
                (hierarchyMode === "region" &&
                  grains.length === 2 &&
                  row.groupType === "region" &&
                  index === 0) ||
                (hierarchyMode === "region" &&
                  grains.length === 2 &&
                  row.groupType === "market" &&
                  index === 1) ||
                (hierarchyMode === "market" &&
                  grains.length === 1 &&
                  index === 0) ||
                (hierarchyMode === "market" &&
                  grains.length === 2 &&
                  row.groupType === "market" &&
                  index === 1)) ? (
                <MaterialIcons
                  name={effectiveExpandedGroups.has(row.id) ? "expand-more" : "chevron-right"}
                  size={14}
                  color="#828282"
                  style={{ marginRight: 4 }}
                />
              ) : null}
              <Text
                style={[
                  row.groupType === "region" && styles.hierarchyLabel,
                  row.groupType === "market" && styles.hierarchyMarketLabel,
                  row.groupType === "location" && styles.hierarchyLocationLabel,
                  // Underline only the hovered cell's text
                  isCellHovered && { textDecorationLine: "underline" },
                ]}
                numberOfLines={1}
              >
                {hierarchyCellLabel(row, grain, hierarchyMode)}
              </Text>
            </View>
          </View>
        );
      })}
      {metrics.map((metric) => {
        const value = metricValue(row, metric.key);
        return (
          <Text
            key={metric.key}
            style={[
              styles.cell,
              styles.metricCell,
              styles.valueCell,
              { width: columnWidths.metric },
              metric.key === "pctNonAsn" &&
                (value > 25 ? styles.danger : styles.good),
            ]}
          >
            {formatMetric(value, metric.key)}
          </Text>
        );
      })}
    </TouchableOpacity>
  );
};

export default LocationSummaryTableRow;
